import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { PaymentService } from '../shared/services/payment.service';
import { Utils } from '../shared/utils/utils';

@Component({
  selector: 'app-payment-requests',
  templateUrl: './payment-requests.component.html',
  styleUrls: ['./payment-requests.component.css']
})
export class PaymentRequestsComponent implements OnInit, OnDestroy {

  busy: Subscription;
  requests: any[] = [];
  filtered: any[] = [];
  paginated: any[] = [];
  searchQuery = '';

  // Stats
  totalEnvoyes = 0;
  totalPaids = 0;
  totalExpires = 0;
  totalAbonnements = 0;

  // Pagination
  currentPage = 1;
  itemsPerPage = 10;
  totalPages = 0;
  pages: number[] = [];

  // Auto-refresh
  private refreshInterval: any;

  constructor(private paymentService: PaymentService) { }

  ngOnInit(): void {
    this.loadRequests();

    // Rafraîchir automatiquement toutes les 30 secondes
    this.refreshInterval = setInterval(() => {
      this.loadRequests();
    }, 30000);
  }

  ngOnDestroy(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  loadRequests(): void {
    this.busy = this.paymentService.getPaymentRequests().subscribe({
      next: (data) => {
        this.requests = data;
        this.filtered = data;
        this.computeStats();
        this.computePagination();
      },
      error: () => {
        Utils.sweetAlert('error', 'Erreur', 'Impossible de charger les données.');
      }
    });
  }

  computeStats(): void {
    this.totalEnvoyes = this.requests.length;

    // Payés / Actifs — basé sur statut du lien
    this.totalPaids = this.requests.filter(r =>
      r.statut === 'paye' || r.statut === 'actif'
    ).length;

    // Non payés / Expirés
    this.totalExpires = this.requests.filter(r =>
      new Date(r.expires_at) < new Date() &&
      r.statut === 'lien_envoye'
    ).length;

    // Abonnements en cours
    this.totalAbonnements = this.requests.filter(r =>
      r.type === 'subscription' && r.statut === 'actif'
    ).length;
  }

  computePagination(): void {
    this.totalPages = Math.ceil(this.filtered.length / this.itemsPerPage);
    this.pages = Array.from({ length: this.totalPages }, (_, i) => i + 1);
    this.updatePaginated();
  }

  updatePaginated(): void {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    this.paginated = this.filtered.slice(start, end);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.updatePaginated();
  }

  onSearch(query: string): void {
    this.searchQuery = query;
    this.currentPage = 1;
    if (!query) {
      this.filtered = this.requests;
    } else {
      const q = query.toLowerCase();
      this.filtered = this.requests.filter(r =>
        r.client.company_name?.toLowerCase().includes(q) ||
        r.client.director_name?.toLowerCase().includes(q) ||
        r.client.email?.toLowerCase().includes(q)
      );
    }
    this.computePagination();
  }

  onItemsPerPageChange(value: number): void {
    this.itemsPerPage = value;
    this.currentPage = 1;
    this.computePagination();
  }

  isExpired(expiresAt: string): boolean {
    return new Date(expiresAt) < new Date();
  }

  getStatutLien(req: any): { label: string; class: string } {
    if (this.isExpired(req.expires_at) && req.statut === 'lien_envoye') {
      return { label: 'Expiré', class: 'badge-expired' };
    }
    if (!this.isExpired(req.expires_at) && req.statut === 'lien_envoye') {
      return { label: 'Actif', class: 'badge-pending' };
    }
    return { label: 'Utilisé', class: 'badge-used' };
  }

  getStatutPaiement(req: any): { label: string; class: string; icon: string } {
    switch (req.statut) {
      case 'paye':
        return { label: 'Payé', class: 'badge-paid', icon: 'fa-check' };
      case 'actif':
        return { label: 'Actif', class: 'badge-actif', icon: 'fa-check-circle' };
      case 'trial':
        return { label: 'Trial', class: 'badge-trial', icon: 'fa-hourglass-half' };
      case 'impaye':
        return { label: 'Impayé', class: 'badge-impaye', icon: 'fa-exclamation-circle' };
      case 'annule':
        return { label: 'Annulé', class: 'badge-cancel', icon: 'fa-times-circle' };
      default:
        return { label: 'En attente', class: 'badge-pending', icon: 'fa-clock' };
    }
  }

  get startIndex(): number {
    return (this.currentPage - 1) * this.itemsPerPage + 1;
  }

  get endIndex(): number {
    return Math.min(this.currentPage * this.itemsPerPage, this.filtered.length);
  }
}